import React, { useContext, useCallback, useEffect, useMemo } from 'react';

import { useFormikContext, getIn } from 'formik';

import { validateSchema } from '../utils';
import FormFieldGroupContext from './FormFieldGroupContext';

export const FormFieldGroup = ({
  children,
  name,
  active = true,
  disabled = false,
  validate,
  validationSchema,
}) => {
  const { getName, isActive, isDisabled } = useContext(FormFieldGroupContext);
  const formik = useFormikContext();
  const {
    submittingMetaRef,
    registerField,
    unregisterField,
    setFieldValue,
    values,
    initialValues,
  } = formik;
  const path = useMemo(() => getName(name), [getName, name]);

  const handleGetName = useCallback((n) => `${path}.${n}`, [path]);
  const handleIsActive = useCallback(
    (a) => isActive(a) && active,
    [active, isActive]
  );
  const handleIsDisabled = useCallback(
    (d) => isDisabled(d) || disabled,
    [disabled, isDisabled]
  );

  const handleSetValue = useCallback(
    (value) => {
      setFieldValue(path, value);
    },
    [path, name]
  );

  const validateFn = useMemo(() => {
    let fn;

    if (typeof validate === 'function') {
      fn = (value) => validate(value, submittingMetaRef.current, values);
    } else if (validationSchema) {
      fn = (value) =>
        validateSchema(value, validationSchema, { context: values });
    }
    return fn;
  }, [validate, validationSchema]);

  const value = useMemo(() => getIn(values, path), [path, values]);
  const initialValue = useMemo(
    () => getIn(initialValues, path),
    [path, initialValues]
  );

  const bag = {
    value,
    initialValue,
    getName: handleGetName,
    setGroupValue: handleSetValue,
    isActive: handleIsActive,
    isDisabled: handleIsDisabled,
    formik: formik,
    name: path,
  };

  useEffect(() => {
    if (name) {
      registerField(name, {
        validate: validateFn,
      });
    }
    return () => {
      if (name) {
        unregisterField(name);
      }
    };
  }, [registerField, unregisterField, name, validateFn]);

  useEffect(() => {
    if (validate) {
      // eslint-disable-next-line
      console.log(`"validate" method is not supported yet`);
    }
  }, [validate]);

  return (
    <FormFieldGroupContext.Provider value={bag}>
      {typeof children === 'function' ? children(bag) : children}
    </FormFieldGroupContext.Provider>
  );
};
